/*
 * Rotas do painel e guarda de acesso.
 *
 * A guarda do frontend e conveniencia de navegacao; a autorizacao real e sempre
 * do servidor (o painel nunca decide sozinho o que pode ser feito).
 */
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactElement } from 'react';
import { AuthProvider, useAuth } from './lib/auth';
import { AppShell } from './components/AppShell';
import { Spinner } from './components/ui';
import { LoginPage } from './routes/LoginPage';
import { DashboardPage } from './routes/DashboardPage';
import { AdminsPage } from './routes/AdminsPage';
import { ChangePasswordPage } from './routes/ChangePasswordPage';
import { UnitsPage } from './routes/UnitsPage';
import { MikrotiksPage } from './routes/MikrotiksPage';
import { CampaignsPage } from './routes/CampaignsPage';
import { TermsPage } from './routes/TermsPage';
import { CustomersPage } from './routes/CustomersPage';
import { SessionsPage } from './routes/SessionsPage';
import { ReportsPage } from './routes/ReportsPage';
import { IntegrationsPage } from './routes/IntegrationsPage';
import { AuditPage } from './routes/AuditPage';

function FullPageLoader() {
  return (
    <div className="grid min-h-full place-items-center">
      <Spinner label="Verificando sessao..." />
    </div>
  );
}

function Protected({ children }: { children: ReactElement }) {
  const { session, loading, mustChangePassword } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageLoader />;
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (mustChangePassword && location.pathname !== '/trocar-senha') {
    return <Navigate to="/trocar-senha" replace />;
  }

  return children;
}

function PublicOnly({ children }: { children: ReactElement }) {
  const { session, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (session) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnly>
            <LoginPage />
          </PublicOnly>
        }
      />

      <Route
        path="/trocar-senha"
        element={
          <Protected>
            <ChangePasswordPage />
          </Protected>
        }
      />

      <Route
        element={
          <Protected>
            <AppShell />
          </Protected>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/mikrotiks" element={<MikrotiksPage />} />
        <Route path="/unidades" element={<UnitsPage />} />
        <Route path="/campanhas" element={<CampaignsPage />} />
        <Route path="/clientes" element={<CustomersPage />} />
        <Route path="/termos" element={<TermsPage />} />
        <Route path="/sessoes" element={<SessionsPage />} />
        <Route path="/relatorios" element={<ReportsPage />} />
        <Route path="/integracoes" element={<IntegrationsPage />} />
        <Route path="/auditoria" element={<AuditPage />} />
        {/* Telas que deixaram de existir. A configuracao do portal passou a
            viver no painel de cada equipamento, ao lado do botao que envia
            para o roteador — que e onde o trabalho realmente acontece. */}
        <Route path="/portal" element={<Navigate to="/mikrotiks" replace />} />
        <Route path="/aparencia" element={<Navigate to="/mikrotiks" replace />} />
        <Route path="/acesso" element={<Navigate to="/mikrotiks" replace />} />
        <Route path="/administradores" element={<AdminsPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
